/**
 * Load More for Collection Pages
 *
 * Replaces automatic infinite scroll with a manual "Veja Mais" button.
 * Uses Section Rendering API to load next pages.
 * Compatible with facets.js (MutationObserver re-initializes after filter/sort).
 * SEO: pagination links remain in the DOM, only hidden visually.
 */
(function () {
  'use strict';

  var SENTINEL_ID = 'lh-scroll-sentinel';
  var GRID_ID = 'product-grid';
  var CONTAINER_ID = 'ProductGridContainer';
  var ITEM_SELECTOR = '.collection__grid__item, .grid-banner';
  var BTN_CLASS = 'lh-load-more-btn';

  var mutationObserver = null;
  var loading = false;

  function getGrid() {
    return document.getElementById(GRID_ID);
  }

  function getSentinel() {
    return document.getElementById(SENTINEL_ID);
  }

  function getSectionId() {
    var grid = getGrid();
    return grid ? grid.dataset.id : null;
  }

  function getNextUrl() {
    var grid = getGrid();
    return grid ? grid.dataset.nextUrl : null;
  }

  function setNextUrl(url) {
    var grid = getGrid();
    if (grid) grid.dataset.nextUrl = url || '';
  }

  function extractNextUrlFromHtml(doc) {
    var newGrid = doc.getElementById(GRID_ID);
    return newGrid ? newGrid.dataset.nextUrl || '' : '';
  }

  function extractItems(doc) {
    var newGrid = doc.getElementById(GRID_ID);
    if (!newGrid) return [];
    return Array.from(newGrid.querySelectorAll(':scope > ' + ITEM_SELECTOR));
  }

  function resetAnimationOrder(items, startIndex) {
    items.forEach(function (item, i) {
      if (item.hasAttribute('data-cascade')) {
        item.style.setProperty('--animation-order', startIndex + i);
      }
    });
  }

  // ─── Button ────────────────────────────────────────────────────────────────

  function getOrCreateButton() {
    var sentinel = getSentinel();
    if (!sentinel) return null;
    var btn = sentinel.querySelector('.' + BTN_CLASS);
    if (btn) return btn;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = BTN_CLASS + ' wt-button wt-button--secondary';
    btn.textContent = 'Veja Mais';
    btn.addEventListener('click', function () { loadNextPage(); });
    sentinel.appendChild(btn);
    return btn;
  }

  function setButtonLoading(btn) {
    if (!btn) return;
    btn.disabled = true;
    btn.classList.add(BTN_CLASS + '--loading');
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:middle;margin-right:6px"><circle cx="12" cy="12" r="10" stroke-dasharray="31.4 31.4" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="0.8s" from="0 12 12" to="360 12 12" repeatCount="indefinite"/></circle></svg>Carregando...';
  }

  function setButtonReady(btn) {
    if (!btn) return;
    btn.disabled = false;
    btn.classList.remove(BTN_CLASS + '--loading');
    btn.textContent = 'Veja Mais';
  }

  function fadeOutButton(btn) {
    if (!btn) return;
    btn.classList.add(BTN_CLASS + '--fade-out');
    btn.addEventListener('transitionend', function () {
      btn.remove();
    }, { once: true });
  }

  // ─── Load ──────────────────────────────────────────────────────────────────

  async function loadNextPage() {
    if (loading) return;

    var nextUrl = getNextUrl();
    if (!nextUrl) return;

    var sectionId = getSectionId();
    if (!sectionId) return;

    var btn = getOrCreateButton();
    loading = true;
    setButtonLoading(btn);

    try {
      var separator = nextUrl.includes('?') ? '&' : '?';
      var fetchUrl = nextUrl + separator + 'section_id=' + sectionId;

      var response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('HTTP ' + response.status);

      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');

      var grid = getGrid();
      if (!grid) return;

      var existingCount = grid.querySelectorAll(ITEM_SELECTOR).length;
      var newItems = extractItems(doc);

      if (newItems.length === 0) {
        setNextUrl('');
        fadeOutButton(btn);
        return;
      }

      resetAnimationOrder(newItems, existingCount + 1);

      var fragment = document.createDocumentFragment();
      newItems.forEach(function (item) {
        fragment.appendChild(item);
      });
      grid.appendChild(fragment);

      var newNextUrl = extractNextUrlFromHtml(doc);
      setNextUrl(newNextUrl);

      // Keep the URL clean
      var url = new URL(window.location.href);
      if (url.searchParams.has('page')) {
        url.searchParams.delete('page');
        history.replaceState(null, '', url.toString());
      }

      if (!newNextUrl) {
        fadeOutButton(btn);
      } else {
        setButtonReady(btn);
      }
    } catch (err) {
      console.error('[lh-load-more] Error loading next page:', err);
      setButtonReady(btn);
    } finally {
      loading = false;
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    loading = false;
    var nextUrl = getNextUrl();
    if (nextUrl) {
      getOrCreateButton();
    }
  }

  function setupMutationObserver() {
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    mutationObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length > 0) {
          init();
          break;
        }
      }
    });

    mutationObserver.observe(container, { childList: true });
  }

  function boot() {
    init();
    setupMutationObserver();
  }

  if (document.readyState === 'complete') {
    requestAnimationFrame(boot);
  } else {
    window.addEventListener('load', function () {
      requestAnimationFrame(boot);
    });
  }
})();
